"use client"

import { AnimatePresence, motion, useReducedMotion } from "motion/react"
import { memo, useCallback, useId, useRef, useState } from "react"
import { cn } from "@strivelabs/ui-core"
import { DURATION_BASE, EASE_OUT } from "@strivelabs/ui-core/v2"
import { useSidebar } from "./context"
import { Disclosure } from "./disclosure"
import { NavSectionLabel } from "./nav-section"
import type { NavNode, NavSection } from "./types"

export interface NavTreeProps {
  sections: NavSection[]
  depth?: number
}

/** Renders a run of sections. Recursion happens through NavTreeItem. */
export function NavTree({ sections, depth = 0 }: NavTreeProps) {
  return (
    <>
      {sections.map(section => (
        <div key={section.id}>
          {section.label ? <NavSectionLabel label={section.label} /> : null}
          <ul className="flex flex-col">
            {section.items.map(item => (
              <NavTreeItem key={item.id} node={item} depth={depth} />
            ))}
          </ul>
        </div>
      ))}
    </>
  )
}

interface NavTreeItemProps {
  node: NavNode
  depth: number
}

const NavTreeItem = memo(function NavTreeItem({ node, depth }: NavTreeItemProps) {
  const { isExpanded, toggleExpanded, currentPath, renderLink } = useSidebar()
  const reduce = useReducedMotion()
  const panelId = useId()
  const [loaded, setLoaded] = useState<NavSection[] | null>(null)
  const [loading, setLoading] = useState(false)
  const requested = useRef(false)

  const expanded = isExpanded(node.id)
  const expandable = node.children !== undefined || node.loadChildren !== undefined
  const children = node.children ?? loaded ?? []

  const active = node.match
    ? node.match(currentPath)
    : node.href !== undefined && node.href !== "#" && currentPath === node.href

  const onToggle = useCallback(() => {
    toggleExpanded(node.id)
    // `requested` rather than `loaded === null`, so a second click while the
    // first fetch is still in flight cannot start a duplicate request.
    if (node.loadChildren && !requested.current) {
      requested.current = true
      setLoading(true)
      node
        .loadChildren()
        .then(sections => setLoaded(sections))
        .catch(() => {
          // Release the guard so the next expand tries again. Without a catch
          // this was an unhandled rejection, and `requested` stayed set — the
          // node was left empty for the rest of the session with no way back.
          requested.current = false
        })
        .finally(() => setLoading(false))
    }
  }, [node, toggleExpanded])

  // Row geometry from the Home Shell design: a 26px row with an 8px radius,
  // a 28×32 icon box at 15px, and a 13px label. Resting ink is 78% for the
  // label and 56% for the icon; the active row lifts both to full ink, sets
  // the label medium, and takes the deeper of the two warm tints so it still
  // reads beside a hovered neighbour.
  const rowClass = cn(
    "group/row flex h-[26px] min-w-0 flex-1 items-center gap-0.5 rounded-v2-lg pr-1 text-[13px] leading-[18px]",
    "text-v2-sidebar-foreground/[0.78] transition-colors duration-v2-quick hover:bg-v2-sidebar-accent",
    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-v2-sidebar-ring",
    active && "bg-v2-sidebar-active font-medium text-v2-sidebar-accent-foreground",
    node.disabled && "pointer-events-none opacity-[0.42]",
  )

  const label = (
    <>
      {node.icon ? (
        <span
          className={cn(
            "grid h-8 w-7 shrink-0 place-items-center text-[15px] leading-none",
            active ? "text-v2-sidebar-accent-foreground" : "text-v2-sidebar-foreground/[0.56]",
          )}
        >
          {node.icon}
        </span>
      ) : null}
      <span className="truncate">{node.label}</span>
    </>
  )

  return (
    <li style={{ paddingInlineStart: depth === 0 ? 0 : 12 }}>
      <div className="flex items-center">
        {expandable ? (
          <Disclosure
            expanded={expanded}
            busy={loading}
            label={node.label}
            controls={expanded && children.length > 0 ? panelId : undefined}
            onToggle={onToggle}
          />
        ) : (
          <span aria-hidden className="w-[14px] shrink-0" />
        )}
        {node.href && node.href !== "#" && !node.disabled
          ? renderLink({
              href: node.href,
              className: rowClass,
              children: label,
              "aria-current": active ? "page" : undefined,
              onClick: node.onSelect,
            })
          : (
            <button
              type="button"
              onClick={node.onSelect}
              disabled={node.disabled}
              className={rowClass}
            >
              {label}
            </button>
          )}
      </div>

      <AnimatePresence initial={false}>
        {expanded && children.length > 0 ? (
          <motion.div
            id={panelId}
            key="children"
            initial={reduce ? { opacity: 0 } : { height: 0, opacity: 0 }}
            animate={reduce ? { opacity: 1 } : { height: "auto", opacity: 1 }}
            exit={reduce ? { opacity: 0 } : { height: 0, opacity: 0 }}
            transition={reduce ? { duration: 0 } : { duration: DURATION_BASE, ease: EASE_OUT }}
            className="overflow-hidden"
          >
            <NavTree sections={children} depth={depth + 1} />
          </motion.div>
        ) : null}
      </AnimatePresence>
    </li>
  )
})
